import { useAccount } from 'wagmi';
import { useQuery } from '@tanstack/react-query';
import BigNumber from 'bignumber.js';
import { INDEXER_API_URL, WORKERS_MAPPING } from 'src/constants';
import { doFetch } from 'src/utils';
import {
  ApiWithdrawalRequest,
  ApiProtocolInfo,
  ApiWalletTransaction,
  ApiOracleInfo,
} from 'src/types';
import { useUserRequestData, useWithdrawalDelay } from './smart-contracts';
import { formatDistance } from 'date-fns';

const _handleResponse = async (response: Response) => {
  const json = await response.json();

  if (json.error) {
    throw new Error(json.error.message);
  }

  return json;
};

const REQUEST_LIMIT = Number(import.meta.env.VITE_REQUEST_LIMIT) || 256;
const SHOW_APR_BOOST = import.meta.env.VITE_SHOW_APR_BOOST === 'true';

export function useOracleInfo() {
  const {
    data = {
      update_interval: import.meta.env.VITE_ORACLE_UPDATE_INTERVAL || 0,
      latest_update_epoch: 0,
      latest_update_started_at: 0,
    },
    isLoading: isLoadingOracleInfo,
    error: oracleInfoError,
  } = useQuery({
    queryKey: ['oracleInfo'],
    queryFn: async (): Promise<ApiOracleInfo> => {
      const response = await fetch(`${INDEXER_API_URL}/oracle_info`);
      return _handleResponse(response);
    },
  });

  const now = Date.now();

  const { withdrawalDelay, isLoadingWithdrawalDelay, withdrawalDelayError } =
    useWithdrawalDelay();

  const projectedUnlock = new BigNumber(data.latest_update_started_at ?? now)
    .plus(new BigNumber(withdrawalDelay).plus(2).times(data.update_interval))
    .times(1000)
    .toNumber();

  return {
    oracleInfo: data,
    requestWaitTimeHR: formatDistance(now, projectedUnlock),
    isLoadingOracleInfo: isLoadingOracleInfo || isLoadingWithdrawalDelay,
    oracleInfoError: oracleInfoError || withdrawalDelayError,
  };
}

export function useWalletRequests() {
  const { walletRequests, isLoadingWalletRequests, walletRequestsError } =
    useUserRequestData({});

  return {
    walletRequests: walletRequests,
    atLimit: walletRequests.length === REQUEST_LIMIT,
    isLoadingWalletRequests: isLoadingWalletRequests,
    walletRequestsError: walletRequestsError,
  };
}

export function useProtocolInfo({
  enabled = true,
}: { enabled?: boolean } = {}) {
  const {
    data: protocolInfo,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['protocolInfo'],
    queryFn: (): Promise<ApiProtocolInfo> =>
      doFetch(`${INDEXER_API_URL}/info?apr_period=1`),
    enabled,
  });

  return {
    protocolInfo: protocolInfo ? (SHOW_APR_BOOST ? protocolInfo : { ...protocolInfo, boost: '0' }) : undefined,
    isLoadingProtocolInfo: isLoading,
    protocolInfoError: error,
  };
}

export function useLifetimeRewards() {
  const { address } = useAccount();

  const {
    data: lifetimeRewards = '0',
    isLoading,
    error,
  } = useQuery({
    queryKey: ['lifetimeRewards', address],
    queryFn: (): Promise<string> =>
      doFetch(`${INDEXER_API_URL}/lifetime_rewards?address=${address}`),
    enabled: !!address,
  });

  return {
    lifetimeRewards,
    lifetimeRewardsFormatted: BigNumber(lifetimeRewards).div(1e18).toFixed(),
    isLoadingLifetimeRewards: isLoading,
    lifetimeRewardsError: error,
  };
}

export function useWalletTransactions() {
  const { address } = useAccount();

  const {
    data: walletTransactions = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['walletTransactions', address],
    queryFn: (): Promise<ApiWalletTransaction[]> =>
      doFetch(`${INDEXER_API_URL}/transactions?address=${address}`),
    enabled: !!address,
  });

  return {
    walletTransactions,
    isLoadingWalletTransactions: isLoading,
    walletTransactionsError: error,
  };
}
